import { pdf } from '@react-pdf/renderer';
import { Checkbox, Form, Modal, Space, theme, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { finalize, from, tap } from 'rxjs';
import SiteMealCategoryMenuPdf from './site-meal-category-menu.component';
import DateRangePicker, { getRangePresets } from '../../../components/controls/date-range-picker';
import FilterSelect from '../../../components/controls/filter-select';
import { AuthContext } from '../../../contexts/auth-context-provider';
import { useDownloadContext } from '../../../contexts/global-download-context-provider';
import {
  type CustomerCycleMenu,
  MealCategory,
  type ScheduledMeal,
  type Site,
} from '../../../entities';
import Customer from '../../../entities/customer';
import { useQuery } from '../../../jsonapi-provider';
import { Roles } from '../../../types';
import { LocalDate } from '../../../utils/local-date';

interface DataType {
  onConfirm: () => void;
  onCancel: () => void;
  customers: Customer[];
}
const serializeCycleMenuFilters = (siteId: string): string | undefined => {
  const parts: string[] = [];
  parts.push(`isEnabled==true`);
  if (siteId) {
    parts.push(`sitesIds=hasmember=${siteId}`);
  }

  return parts.length ? parts.join(';') : undefined;
};
const serializeScheduledMealFilters = (filters: {
  startDate?: LocalDate;
  endDate?: LocalDate;
  site?: string;
  mealCategory?: string;
}): string => {
  const parts: string[] = [];
  parts.push('cycleMenu.isEnabled==true');
  if (filters.startDate && filters.endDate) {
    parts.push(
      `day=ge=${filters?.startDate.toISOString()};day=le=${filters?.endDate.toISOString()}`
    );
  }

  if (filters?.site) {
    parts.push(`cycleMenu.sites.id=hasmember=${filters.site}`);
  }
  if (filters?.mealCategory) {
    parts.push(`mealCategory.id==${filters.mealCategory}`);
  }
  return parts.join(';');
};
const getCurrentCycle = (cycleMenus: CustomerCycleMenu[]): CustomerCycleMenu | undefined => {
  const currentDate = new LocalDate();
  return cycleMenus.find((cm) => {
    return cm.startDate.isBefore(currentDate) && cm.repeatUntil?.isAfter(currentDate);
  });
};
const getNextCycleMenu = (cycleMenus: CustomerCycleMenu[]): CustomerCycleMenu | undefined => {
  const currentDate = new LocalDate();
  return cycleMenus.find((cm) => {
    return cm.startDate.isAfter(currentDate);
  });
};
const getPreviousCycleMenu = (cycleMenus: CustomerCycleMenu[]): CustomerCycleMenu | undefined => {
  const currentDate = new LocalDate();
  return cycleMenus.findLast((cm) => {
    return cm.effectiveEndDate.isBefore(currentDate);
  });
};
const getSecondToLastCycleMenu = (
  cycleMenus: CustomerCycleMenu[]
): CustomerCycleMenu | undefined => {
  const previousCycle = getPreviousCycleMenu(cycleMenus);
  if (!previousCycle) {
    return undefined;
  }
  return cycleMenus.findLast((cm) => {
    return cm.effectiveEndDate.isBefore(previousCycle.startDate);
  });
};
const CycleMenuDownloadModal = forwardRef((props: DataType, ref) => {
  const { token } = theme.useToken();
  const { session } = useContext(AuthContext);
  const { onConfirm, onCancel, customers } = props;
  const [selectedSite, setSelectedSite] = useState<string>();
  const { isDownloading, setDownloading } = useDownloadContext();
  const [selectedMealCategory, setSelectedMealCategory] = useState<string>();
  const [dateRange, setDateRange] = useState<[LocalDate, LocalDate] | undefined>(
    getRangePresets()
      .find((preset) => preset.key === 'm')
      ?.value.map((d) => new LocalDate(d)) as [LocalDate, LocalDate]
  );
  const [isOpen, setIsOpen] = useState(false);
  const scheduledMeals = useQuery<ScheduledMeal[]>(
    selectedMealCategory &&
      selectedSite &&
      dateRange &&
      isOpen && [
        'scheduled-meal',
        {
          filter: serializeScheduledMealFilters({
            startDate: dateRange?.[0],
            endDate: dateRange?.[1],
            site: selectedSite,
            mealCategory: selectedMealCategory,
          }),
          include: ['mealCategory', 'extraMealItem1', 'extraMealItem2'],
        },
      ]
  );
  const cycleMenus = useQuery<CustomerCycleMenu[]>([
    'customer-cycle-menu',
    {
      filter: serializeCycleMenuFilters(selectedSite ?? ''),
      page: {
        size: 4,
      },
      sort: 'startDate',
    },
  ]);
  useImperativeHandle(
    ref,
    () => ({
      showModal: () => {
        setIsOpen(true);
      },
      hideModal: () => {
        setIsOpen(false);
      },
    }),
    []
  );
  const mealCategories = useQuery<MealCategory[]>([
    'meal-category',
    {
      sort: 'sortOrder',
    },
  ]);
  const showMealItemPortions = useRef<boolean>(false);

  const downloadCycleMenu = (): void => {
    setDownloading(true);
    from(
      pdf(
        scheduledMeals.data && (
          <SiteMealCategoryMenuPdf
            startDate={dateRange ? dateRange[0] : new LocalDate()}
            endDate={(dateRange ? dateRange[1] : new LocalDate()).endOf('M')}
            data={scheduledMeals.data}
            site={selectedSite ?? ''}
            mealCategory={
              mealCategories.data?.find((mc) => mc.id === selectedMealCategory) ??
              new MealCategory()
            }
            customer={
              customers.find((cus) => cus.sites.some((site: Site) => site.id === selectedSite)) ??
              new Customer()
            }
            showMealItemPortions={showMealItemPortions.current}
          />
        )
      ).toBlob()
    )
      .pipe(
        tap((blob) => {
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `${customers
            ?.map((cust) => cust.sites.map((site) => site))
            .flat()
            ?.find((site) => site.id === selectedSite)?.name} - ${mealCategories.data?.find(
            (mc) => mc.id === selectedMealCategory
          )?.name} - ${dateRange?.[0].toISOString()} - ${dateRange?.[1].toISOString()}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }),
        finalize(() => {
          setDownloading(false);
        })
      )
      .subscribe();
  };
  useEffect(() => {
    if (mealCategories.data) {
      setSelectedMealCategory(mealCategories.data?.[0]?.id);
    }
  }, [mealCategories.data]);
  useEffect(() => {
    if (customers) {
      setSelectedSite(
        customers
          .map((cust) =>
            cust.sites
              .filter((site) =>
                session?.claims.role === Roles.ROLE_HHC_ADMIN ? true : site.isActive
              )
              .sort((a, b) => a.name.localeCompare(b.name))
          )
          .flat()[0]?.id
      );
    }
  }, [customers]);
  return (
    <>
      <Modal
        title={
          <Typography.Title
            style={{ marginBlockStart: 0, marginBlockEnd: token.marginLG }}
            level={3}
          >
            Cycle Menus
          </Typography.Title>
        }
        open={isOpen}
        okButtonProps={{
          disabled:
            !(selectedSite && selectedMealCategory && dateRange) ||
            scheduledMeals.isLoading ||
            isDownloading,
          loading: mealCategories.isLoading || scheduledMeals.isLoading,
        }}
        onOk={() => {
          downloadCycleMenu();
          onConfirm();
        }}
        onCancel={onCancel}
        okText="Download"
        width={500}
        loading={mealCategories.isLoading}
        forceRender
      >
        {customers && customers.length > 0 && (
          <Space direction="vertical">
            <FilterSelect
              value={selectedSite}
              mode="single"
              options={customers
                .map((cust) =>
                  cust.sites
                    .filter((site) =>
                      session?.claims.role === Roles.ROLE_HHC_ADMIN ? true : site.isActive
                    )
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((site) => ({ label: site.name, value: site.id }))
                )
                .flat()}
              title="Site"
              onChange={(value) => {
                setSelectedSite(value);
              }}
            />
            <FilterSelect
              value={selectedMealCategory}
              mode="single"
              options={mealCategories.data?.map((mc) => ({ label: mc.name, value: mc.id }))}
              title="Meal category"
              onChange={(value) => {
                setSelectedMealCategory(value);
              }}
            />
            <DateRangePicker
              title={'Date Range'}
              value={dateRange?.map((d) => d.toDayjs()) as [Dayjs, Dayjs]}
              onChange={(value) => {
                setDateRange(value?.map((v) => new LocalDate(v)) as [LocalDate, LocalDate]);
              }}
              minDate={dayjs().startOf('y').subtract(1, 'y')}
              maxDate={dayjs().endOf('y').add(1, 'y')}
              showOnlyDateRange={true}
              customRangePreset={getRangePresets()
                .concat(
                  getCurrentCycle(cycleMenus.data ?? [])
                    ? [
                        {
                          label: 'Current Cycle',
                          value: [
                            getCurrentCycle(cycleMenus.data ?? [])?.startDate.toDayjs() ??
                              new LocalDate().endOf('M').toDayjs(),
                            getCurrentCycle(cycleMenus.data ?? [])?.effectiveEndDate.toDayjs() ??
                              new LocalDate().endOf('M').toDayjs(),
                          ],
                          key: 'this-cycle-menu',
                        },
                      ]
                    : []
                )
                .concat(
                  getNextCycleMenu(cycleMenus.data ?? [])
                    ? [
                        {
                          label: 'Next Cycle',
                          value: [
                            getNextCycleMenu(cycleMenus.data ?? [])?.startDate.toDayjs() ??
                              new LocalDate().endOf('M').toDayjs(),
                            getNextCycleMenu(cycleMenus.data ?? [])?.effectiveEndDate.toDayjs() ??
                              new LocalDate().endOf('M').toDayjs(),
                          ],
                          key: 'next-cycle-menu',
                        },
                      ]
                    : []
                )
                .concat(
                  getPreviousCycleMenu(cycleMenus.data ?? [])
                    ? [
                        {
                          label: 'Last Cycle',
                          value: [
                            getPreviousCycleMenu(cycleMenus.data ?? [])?.startDate.toDayjs() ??
                              new LocalDate().endOf('M').toDayjs(),
                            getPreviousCycleMenu(
                              cycleMenus.data ?? []
                            )?.effectiveEndDate.toDayjs() ?? new LocalDate().endOf('M').toDayjs(),
                          ],
                          key: 'last-cycle-menu',
                        },
                      ]
                    : []
                )
                .concat(
                  getSecondToLastCycleMenu(cycleMenus.data ?? [])
                    ? [
                        {
                          label: 'Second to Last Cycle',
                          value: [
                            getSecondToLastCycleMenu(cycleMenus.data ?? [])?.startDate.toDayjs() ??
                              new LocalDate().endOf('M').toDayjs(),
                            getSecondToLastCycleMenu(
                              cycleMenus.data ?? []
                            )?.effectiveEndDate.toDayjs() ?? new LocalDate().endOf('M').toDayjs(),
                          ],
                          key: 'second-to-last-cycle-menu',
                        },
                      ]
                    : []
                )}
            />
            <Form.Item label="Show meal item portions" colon={false} layout="horizontal">
              <Checkbox
                defaultChecked={false}
                onChange={(value) => {
                  showMealItemPortions.current = value.target.checked;
                }}
              />
            </Form.Item>
          </Space>
        )}
      </Modal>
    </>
  );
});

CycleMenuDownloadModal.displayName = 'CycleMenuDownloadModal';
export default CycleMenuDownloadModal;
